/**
 * Routes the mystery box through the server.
 *
 * The legacy box (core.js:7010-7050) rolls `100 * Math.random()` in the browser,
 * decides the prize, and then pays itself — so it can be re-rolled until the
 * jackpot comes up, and the payout path can be called without the spend.
 *
 * `open_mystery_box()` does the roll, the spend and the award in one
 * transaction, and never returns the roll, so there is nothing to retry
 * against.
 *
 * Seam note: unlike the other economy calls there is no global to wrap — the
 * box is a click handler bound inside `renderShop`'s closure. Replacing the
 * button node with a clone is the only way to drop the original listener, since
 * we hold no reference to it.
 *
 * The scrim reuses the design system's `.modal-overlay` rather than a bespoke
 * one. An earlier version of this file carried its own copy at `z-index: 10002`
 * to clear a grain overlay that used to sit at 9999; the design-system rebuild
 * moved that overlay to `z-index: 0`, so the high value was both redundant and
 * wrong — it painted over the alpha banner.
 */

import { onRendered, legacy, toast } from '../../runtime/legacy.js';

const RARITY_COLOURS = {
  common: '#9e9e9e',
  rare: '#2196F3',
  epic: '#CE93D8',
  legendary: '#FFD700',
};

const REFUSALS = {
  insufficient_tokens: 'Need 30 ⬡ tokens for a Mystery Box!',
  not_authenticated: 'Sign in to open a Mystery Box.',
};

function overlay() {
  const el = document.createElement('div');
  el.className = 'modal-overlay';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', 'Mystery box');
  el.innerHTML = `
    <div class="prize-reveal">
      <div class="prize-reveal-icon" aria-hidden="true">🎁</div>
      <div class="prize-reveal-name">Opening Mystery Box…</div>
    </div>`;
  document.body.appendChild(el);
  return el;
}

function showPrize(el, { icon, tier, tierColour, title, body }, onClose) {
  el.innerHTML = `
    <div class="prize-reveal">
      <div class="prize-reveal-icon" aria-hidden="true">${icon}</div>
      <div class="prize-reveal-tier" style="color:${tierColour};">${tier}</div>
      <div class="prize-reveal-name">${title}</div>
      <div class="prize-reveal-desc">${body}</div>
      <button class="btn btn-primary" id="mystery-close">Awesome!</button>
    </div>`;
  const close = () => {
    el.remove();
    onClose();
  };
  el.querySelector('#mystery-close').addEventListener('click', close);
  // A modal that can only be dismissed by one button is a trap if that button
  // ever fails to render; Escape and a scrim click are the conventional outs.
  el.addEventListener('click', (e) => { if (e.target === el) close(); });
  document.addEventListener('keydown', function esc(e) {
    if (e.key === 'Escape') { document.removeEventListener('keydown', esc); close(); }
  });
  el.querySelector('#mystery-close').focus();
}

async function openBox() {
  const AppState = legacy('AppState');
  const supabase = legacy('supabaseClient');
  const el = overlay();

  // The spinner is theatre, but removing it entirely would make the box feel
  // broken. Run it alongside the request rather than before it.
  const [settled] = await Promise.all([
    supabase.rpc('open_mystery_box').then(
      (r) => r,
      (err) => ({ error: err }),
    ),
    new Promise((r) => setTimeout(r, 1400)),
  ]);

  const close = () => {
    const update = legacy('updateTokenDisplay');
    if (typeof update === 'function') update();
    // Re-render the shop so the new cosmetic and balance appear.
    const handle = legacy('handleRoute');
    if (typeof handle === 'function') handle();
  };

  if (settled.error) {
    console.error('[ironpact] open_mystery_box failed', settled.error);
    el.remove();
    toast('Could not open the box — your tokens are untouched.', 'error', 4000);
    return;
  }

  const result = settled.data;
  if (!result || result.ok !== true) {
    el.remove();
    toast(REFUSALS[result?.error] || 'Could not open the box.', 'error', 4000);
    return;
  }

  if (typeof result.balance === 'number') {
    AppState.tokenBalance = result.balance;
    AppState.profile.token_balance = result.balance;
  }

  if (result.prize === 'tokens') {
    const tiers = {
      500: { tier: 'LEGENDARY', icon: '💰', title: 'Token Jackpot', colour: '#FFD700' },
      200: { tier: 'EPIC', icon: '⬡', title: 'Token Vault', colour: '#CE93D8' },
      75: { tier: 'RARE', icon: '⬡', title: 'Token Cache', colour: '#2196F3' },
    };
    const t = tiers[result.amount] || { tier: 'BONUS', icon: '⬡', title: 'Tokens', colour: '#2196F3' };
    showPrize(el, {
      icon: t.icon,
      tier: t.tier,
      tierColour: t.colour,
      title: t.title,
      body: `+${result.amount} tokens added to your balance.`,
    }, close);
    return;
  }

  // Cosmetic. The server returns an id only; icon/name/description already live
  // in AppState, which is why open_mystery_box needs no columns beyond id and
  // rarity.
  const pool = AppState.cosmetics?.length ? AppState.cosmetics : (legacy('seedCosmetics')?.() ?? []);
  const item = pool.find((c) => c.id === result.cosmetic_id);

  if (item && !(AppState.userCosmetics || []).some((uc) => uc.cosmetic_id === item.id)) {
    AppState.userCosmetics = AppState.userCosmetics || [];
    AppState.userCosmetics.push({ cosmetic_id: item.id, user_id: AppState.user.id });
  }

  showPrize(el, {
    icon: item?.icon || '🎁',
    tier: result.rarity || 'common',
    tierColour: RARITY_COLOURS[result.rarity] || '#fff',
    title: item?.name || 'Cosmetic unlocked',
    body: item?.description || 'Added to your collection.',
  }, close);
}

export function installMysteryBox() {
  onRendered('#mystery-box-btn', (btn) => {
    if (btn.dataset.serverBox === '1') return;

    // We do not hold the original listener, so cloning is the only way to drop
    // it. The clone re-triggers this observer, hence the flag.
    const clone = btn.cloneNode(true);
    clone.dataset.serverBox = '1';
    btn.replaceWith(clone);

    clone.addEventListener('click', async () => {
      if (legacy('isDemoMode') || !legacy('supabaseClient') || !legacy('AppState')?.user) {
        toast('Mystery boxes are unavailable in demo mode.', 'info');
        return;
      }
      clone.disabled = true;
      try {
        await openBox();
      } finally {
        clone.disabled = false;
      }
    });
  });
}
